import { Routes } from '@angular/router';
import { MainLayoutComponent } from './shared/layouts/main-layout/main-layout.component';
import { AuthGuard } from './core/guards/auth-guard.service';
import { LoginComponent } from './features/auth/login/login.component';
import { permissionGuard } from './core/guards/permission.guard';
import { PERMISSIONS_KEYS } from './core/utils/permissions';
import { environment } from '../environments/environment';
import { printDocumentGuard } from './core/guards/print-document.guard';

export const routes: Routes = [
  {
    path: '',
    component: LoginComponent,
  },
  ...(!environment.production
    ? [
        {
          path: 'print/stores/:storeId/invoices/internal/:id',
          loadComponent: () =>
            import(
              './features/print/store-invoice-print-page/store-invoice-print-page.component'
            ).then((m) => m.StoreInvoicePrintPageComponent),
          canActivate: [AuthGuard],
          data: { invoiceType: 'internal' },
        },
        {
          path: 'print/stores/:storeId/invoices/external/:id',
          loadComponent: () =>
            import(
              './features/print/store-invoice-print-page/store-invoice-print-page.component'
            ).then((m) => m.StoreInvoicePrintPageComponent),
          canActivate: [AuthGuard],
          data: { invoiceType: 'external' },
        },
      ]
    : []),
  {
    path: 'print/laundries/invoices/:invoiceId',
    loadComponent: () =>
      import(
        './features/print/receipt-print-page/receipt-print-page.component'
      ).then((m) => m.ReceiptPrintPageComponent),
    canActivate: [AuthGuard, printDocumentGuard],
    data: {
      documentType: 'invoice',
      permissionIds: [
        PERMISSIONS_KEYS.access_laundriesBills,
        PERMISSIONS_KEYS.access_laundriesOrders,
        PERMISSIONS_KEYS.access_laundriesReports,
      ],
    },
  },
  {
    path: 'print/laundries/orders/:orderNumber',
    loadComponent: () =>
      import(
        './features/print/receipt-print-page/receipt-print-page.component'
      ).then((m) => m.ReceiptPrintPageComponent),
    canActivate: [AuthGuard, printDocumentGuard],
    data: {
      documentType: 'order',
      permissionIds: [
        PERMISSIONS_KEYS.access_laundriesBills,
        PERMISSIONS_KEYS.access_laundriesOrders,
        PERMISSIONS_KEYS.access_laundriesReports,
      ],
    },
  },
  {
    path: 'dashboard',
    component: MainLayoutComponent,
    canActivate: [AuthGuard],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./features/home/home.component').then((m) => m.HomeComponent),
        canActivate: [permissionGuard],
        data: {
          permissionId: PERMISSIONS_KEYS.access_homepage,
        },
      },
      {
        path: 'profile',
        loadComponent: () =>
          import('./features/profile/profile.component').then(
            (m) => m.ProfileComponent
          ),
        data: { breadcrumbs: [{ label: 'shared/profile', link: 'profile' }] },
      },
      {
        path: 'notification',
        loadComponent: () =>
          import('./features/notification/all/all.component').then(
            (m) => m.AllComponent
          ),
        data: {
          breadcrumbs: [
            { label: 'shared/notifications', link: 'notification' },
          ],
        },
      },
      {
        path: 'companies',
        loadChildren: () =>
          import('./features/companies/companies.routes').then((m) => m.routes),
        data: {
          breadcrumbs: [{ label: 'shared/companies', link: 'companies' }],
        },
      },
      {
        path: 'company-offers',
        loadChildren: () =>
          import('./features/company-offers/company-offers.routes').then(
            (m) => m.routes
          ),
        data: {
          breadcrumbs: [
            { label: 'shared/companiesOffers', link: 'company-offers' },
          ],
        },
      },
      {
        path: 'settings/cities',
        loadComponent: () =>
          import('./features/settings/cities/cities.component').then(
            (m) => m.CitiesComponent
          ),
        canActivate: [permissionGuard],
        data: {
          permissionId: PERMISSIONS_KEYS.access_settingsCities,
          breadcrumbs: [{ label: 'shared/cities', link: 'settings/cities' }],
        },
      },
      {
        path: 'settings/offer-categories',
        loadComponent: () =>
          import(
            './features/settings/offer-categories/offer-categories.component'
          ).then((m) => m.OfferCategoriesComponent),
        canActivate: [permissionGuard],
        data: {
          permissionId: PERMISSIONS_KEYS.access_settingsOfferCategories,
          breadcrumbs: [
            {
              label: 'shared/offerCategories',
              link: 'settings/offer-categories',
            },
          ],
        },
      },
      {
        path: 'settings/pages',
        loadChildren: () =>
          import('./features/settings/pages/pages.routes').then(
            (m) => m.routes
          ),
      },
      {
        path: 'settings/email',
        loadComponent: () =>
          import('./features/settings/email/email.component').then(
            (m) => m.EmailComponent
          ),
        canActivate: [permissionGuard],
        data: {
          permissionId: PERMISSIONS_KEYS.access_settingsEmail,
          breadcrumbs: [
            {
              label: 'shared/email',
              link: 'settings/email',
            },
          ],
        },
      },
      {
        path: 'settings/message-entites',
        loadComponent: () =>
          import(
            './features/settings/message-entites/message-entites.component'
          ).then((m) => m.MessageEntitesComponent),
        canActivate: [permissionGuard],
        data: {
          permissionId: PERMISSIONS_KEYS.access_settingsMessageEntites,
          breadcrumbs: [
            {
              label: 'shared/messageEntites',
              link: 'settings/message-entites',
            },
          ],
        },
      },
      {
        path: 'settings/payment-methods',
        loadComponent: () =>
          import(
            './features/settings/payment-methods/payment-methods.component'
          ).then((m) => m.PaymentMethodsComponent),
        canActivate: [permissionGuard],
        data: {
          permissionId: PERMISSIONS_KEYS.access_settingsPaymentMethods,
          breadcrumbs: [
            {
              label: 'shared/paymentMethods',
              link: 'settings/payment-methods',
            },
          ],
        },
      },
      {
        path: 'messages',
        loadChildren: () =>
          import('./features/messages/messages.routes').then((m) => m.routes),
        data: {
          breadcrumbs: [{ label: 'shared/correspondences', link: 'messages' }],
        },
      },
      ...(!environment.production
        ? [
            {
              path: 'settings/wallet-settings/edit-wallet',
              loadComponent: () =>
                import(
                  './features/settings/wallet-settings/edit-wallet/edit-wallet.component'
                ).then((m) => m.EditWalletComponent),
              canActivate: [permissionGuard],
              data: {
                permissionId:
                  PERMISSIONS_KEYS.access_settingsWalletSettingsEditWallet,
                breadcrumbs: [
                  {
                    label: 'shared/editWallet',
                    link: 'settings/wallet-settings/edit-wallet',
                  },
                ],
              },
            },
            {
              path: 'settings/wallet-settings/wallet-cycle',
              loadComponent: () =>
                import(
                  './features/settings/wallet-settings/wallet-cycle/wallet-cycle.component'
                ).then((m) => m.WalletCycleComponent),
              canActivate: [permissionGuard],
              data: {
                permissionId:
                  PERMISSIONS_KEYS.access_settingsWalletSettingsWalletCycle,
                breadcrumbs: [
                  {
                    label: 'shared/walletCycle',
                    link: 'settings/wallet-settings/wallet-cycle',
                  },
                ],
              },
            },
            {
              path: 'settings/wallet-settings/financial-report',
              loadChildren: () =>
                import(
                  './features/settings/wallet-settings/financial-report/financial-report.routes'
                ).then((m) => m.routes),
              canActivate: [permissionGuard],
              data: {
                breadcrumbs: [
                  {
                    label: 'shared/exportReport',
                    link: 'settings/wallet-settings/financial-report',
                  },
                ],
              },
            },
            {
              path: 'settings/wallet-settings/export-operations-report',
              loadComponent: () =>
                import(
                  './features/settings/wallet-settings/export-operations-report/export-operations-report.component'
                ).then((m) => m.ExportOperationsReportComponent),
              canActivate: [permissionGuard],
              data: {
                permissionId:
                  PERMISSIONS_KEYS.access_settingsWalletSettingsExportOperationsReport,
                breadcrumbs: [
                  {
                    label: 'shared/exportOperationsReport',
                    link: 'settings/wallet-settings/export-operations-report',
                  },
                ],
              },
            },
            {
              path: 'settings/wallet-settings/payment-operations-report',
              loadComponent: () =>
                import(
                  './features/settings/wallet-settings/payment-operations-report/payment-operations-report.component'
                ).then((m) => m.PaymentOperationsReportComponent),
              canActivate: [permissionGuard],
              data: {
                permissionId:
                  PERMISSIONS_KEYS.access_settingsWalletSettingsPaymentOperationsReport,
                breadcrumbs: [
                  {
                    label: 'shared/paymentOperationsReport',
                    link: 'settings/wallet-settings/payment-operations-report',
                  },
                ],
              },
            },
            {
              path: 'settings/wallet-settings/admin-procedures',
              loadComponent: () =>
                import(
                  './features/settings/wallet-settings/admin-procedures/admin-procedures.component'
                ).then((m) => m.AdminProceduresComponent),
              canActivate: [permissionGuard],
              data: {
                permissionId:
                  PERMISSIONS_KEYS.access_settingsWalletSettingsAdminProcedures,
                breadcrumbs: [
                  {
                    label: 'shared/adminProcedures',
                    link: 'settings/wallet-settings/admin-procedures',
                  },
                ],
              },
            },
            {
              path: 'settings/wallet-settings/beneficiaries-info-wallet',
              loadComponent: () =>
                import(
                  './features/settings/wallet-settings/beneficiaries-info-wallet/beneficiaries-info-wallet.component'
                ).then((m) => m.BeneficiariesInfoWalletComponent),
              canActivate: [permissionGuard],
              data: {
                permissionId:
                  PERMISSIONS_KEYS.access_settingsWalletSettingsBeneficiariesInfoWallet,
                breadcrumbs: [
                  {
                    label: 'shared/beneficiariesInfoWallet',
                    link: 'settings/wallet-settings/beneficiaries-info-wallet',
                  },
                ],
              },
            },
          ]
        : []),
      ...(environment.production
        ? []
        : [
            {
              path: 'stores/stores',
              loadChildren: () =>
                import('./features/stores/stores/stores.routes').then(
                  (m) => m.routes
                ),
              data: {
                breadcrumbs: [
                  {
                    label: 'stores/stores',
                    link: 'stores/stores',
                  },
                ],
              },
            },
            {
              path: 'stores/reports',
              loadComponent: () =>
                import(
                  './features/stores/store-reports/store-reports.component'
                ).then((m) => m.StoreReportsComponent),
              data: {
                breadcrumbs: [
                  {
                    label: 'shared/reports',
                    link: 'stores/reports',
                  },
                ],
              },
            },
            {
              path: 'stores/store-activities',
              loadComponent: () =>
                import(
                  './features/stores/store-activities/store-activities.component'
                ).then((m) => m.StoreActivitiesComponent),
              data: {
                breadcrumbs: [
                  {
                    label: 'stores/storeActivities',
                    link: 'stores/store-activities',
                  },
                ],
              },
            },
            {
              path: 'stores/settings/return-policy',
              loadComponent: () =>
                import(
                  './features/stores/settings/return-policy/return-policy.component'
                ).then((m) => m.ReturnPolicyComponent),
              data: {
                breadcrumbs: [
                  {
                    label: 'shared/settings',
                    link: 'stores/settings/return-policy',
                  },
                  {
                    label: 'stores/returnPolicy',
                    link: 'stores/settings/return-policy',
                  },
                ],
              },
            },
          ]),
      {
        path: 'laundries/laundries',
        loadChildren: () =>
          import('./features/laundries/laundries/laundries.routes').then(
            (m) => m.routes
          ),
        data: {
          breadcrumbs: [
            { label: 'shared/laundries', link: 'laundries/laundries' },
          ],
        },
      },
      {
        path: 'laundries/branches',
        loadChildren: () =>
          import('./features/laundries/branches/branches.routes').then(
            (m) => m.routes
          ),
        data: {
          breadcrumbs: [
            { label: 'shared/branches', link: 'laundries/branches' },
          ],
        },
      },
      {
        path: 'laundries/collection-points',
        loadChildren: () =>
          import(
            './features/laundries/collection-points/collection-points.routes'
          ).then((m) => m.routes),
        data: {
          breadcrumbs: [
            {
              label: 'shared/collectionPoints',
              link: 'laundries/collection-points',
            },
          ],
        },
      },
      {
        path: 'laundries/addition-requests',
        loadChildren: () =>
          import(
            './features/laundries/addition-requests/addition-requests.routes'
          ).then((m) => m.routes),
        data: {
          breadcrumbs: [
            {
              label: 'shared/additionRequests',
              link: 'laundries/addition-requests',
            },
          ],
        },
      },
      {
        path: 'laundries/employees',
        loadChildren: () =>
          import('./features/laundries/employees/employees.routes').then(
            (m) => m.routes
          ),
        data: {
          breadcrumbs: [
            { label: 'shared/employees', link: 'laundries/employees' },
          ],
        },
      },
      {
        path: 'laundries/audit-history',
        loadComponent: () =>
          import(
            './features/laundries/audit-history/audit-history.component'
          ).then((m) => m.AuditHistoryComponent),
        canActivate: [permissionGuard],
        data: {
          permissionId: PERMISSIONS_KEYS.access_laundriesAuditHistory,
          breadcrumbs: [
            { label: 'shared/auditHistory', link: 'laundries/audit-history' },
          ],
        },
      },
      {
        path: 'laundries/laundry-categories',
        loadComponent: () =>
          import(
            './features/laundries/laundry-category/laundry-category.component'
          ).then((m) => m.LaundryCategoryComponent),
        canActivate: [permissionGuard],
        data: {
          permissionId: PERMISSIONS_KEYS.access_laundriesLaundryCategories,
          breadcrumbs: [
            {
              label: 'shared/laundryCategories',
              link: 'laundries/laundry-categories',
            },
          ],
        },
      },
      {
        path: 'laundries/services',
        loadComponent: () =>
          import('./features/laundries/services/services.component').then(
            (m) => m.ServicesComponent
          ),
        canActivate: [permissionGuard],
        data: {
          permissionId: PERMISSIONS_KEYS.access_laundriesServices,
          breadcrumbs: [
            { label: 'shared/services', link: 'laundries/services' },
          ],
        },
      },
      {
        path: 'laundries/orders',
        loadComponent: () =>
          import('./features/laundries/orders/orders.component').then(
            (m) => m.OrdersComponent
          ),
        canActivate: [permissionGuard],
        data: {
          permissionId: PERMISSIONS_KEYS.access_laundriesOrders,
          breadcrumbs: [{ label: 'shared/orders', link: 'laundries/orders' }],
        },
      },
      {
        path: 'laundries/bills',
        loadComponent: () =>
          import('./features/laundries/bills/bills.component').then(
            (m) => m.BillsComponent
          ),
        canActivate: [permissionGuard],
        data: {
          permissionId: PERMISSIONS_KEYS.access_laundriesBills,
          breadcrumbs: [{ label: 'shared/bills', link: 'laundries/bills' }],
        },
      },
      {
        path: 'laundries/packages/overview',
        loadChildren: () =>
          import(
            './features/laundries/packages/overview-packages/packages.routes'
          ).then((m) => m.routes),
        data: {
          breadcrumbs: [
            {
              label: 'shared/overviewPackages',
              link: 'laundries/packages/overview',
            },
          ],
        },
      },
      {
        path: 'laundries/packages/subscriptions',
        loadComponent: () =>
          import(
            './features/laundries/packages/packages-subscriptions/packages-subscriptions.component'
          ).then((m) => m.PackagesSubscriptionsComponent),
        canActivate: [permissionGuard],
        data: {
          permissionId: PERMISSIONS_KEYS.access_packagesSubscriptions,
          breadcrumbs: [
            {
              label: 'shared/packagesSubscriptions',
              link: 'laundries/packages/subscriptions',
            },
          ],
        },
      },
      {
        path: 'laundries/packages/offers-increases',
        loadChildren: () =>
          import(
            './features/laundries/packages/offers-increases/offers-increases.routes'
          ).then((m) => m.routes),
        data: {
          breadcrumbs: [
            {
              label: 'shared/offersIncreases',
              link: 'laundries/packages/offers-increases',
            },
          ],
        },
      },
      {
        path: 'laundries/reports',
        loadComponent: () =>
          import(
            './features/laundries/laundry-reports/laundry-reports.component'
          ).then((m) => m.LaundryReportsComponent),
        canActivate: [permissionGuard],
        data: {
          permissionId: PERMISSIONS_KEYS.access_laundriesReports,
          breadcrumbs: [
            { label: 'laundries/laundryReports', link: 'laundries/reports' },
          ],
        },
      },
      {
        path: 'laundries/settings/privileges',
        loadComponent: () =>
          import(
            './features/laundries/settings/privileges/privileges.component'
          ).then((m) => m.PrivilegesComponent),
        canActivate: [permissionGuard],
        data: {
          permissionId: PERMISSIONS_KEYS.access_laundrySettingsPrivileges,
          breadcrumbs: [
            {
              label: 'shared/privileges',
              link: 'laundries/settings/privileges',
            },
          ],
        },
      },
      {
        path: 'laundries/settings/order-rules',
        loadChildren: () =>
          import(
            './features/laundries/settings/order-rules/order-rules.routes'
          ).then((m) => m.routes),
        data: {
          breadcrumbs: [
            {
              label: 'shared/orderRules',
              link: 'laundries/settings/order-rules',
            },
          ],
        },
      },
      {
        path: 'laundries/settings/working-hours',
        loadComponent: () =>
          import(
            './features/laundries/settings/working-hours/working-hours.component'
          ).then((m) => m.WorkingHoursComponent),
        canActivate: [permissionGuard],
        data: {
          permissionId: PERMISSIONS_KEYS.access_settingsWorkingHours,
          breadcrumbs: [
            {
              label: 'shared/workingHours',
              link: 'laundries/settings/working-hours',
            },
          ],
        },
      },
      ...(environment.production
        ? []
        : [
            {
              path: 'loans/overview-loan-requests',
              loadChildren: () =>
                import(
                  './features/loans/loan-requests/overview/overview-loan-requests.route'
                ).then((m) => m.routes),
              data: {
                breadcrumbs: [
                  {
                    label: 'shared/overviewLoanRequests',
                    link: 'loans/overview-loan-requests',
                  },
                ],
              },
            },
            // {
            //   path: 'loans/modification-exceptions',
            //   loadChildren: () =>
            //     import(
            //       './features/loans/loan-requests/modification-exceptions/modification-exceptions.route'
            //     ).then((m) => m.routes),
            //   data: {
            //     breadcrumbs: [
            //       {
            //         label: 'shared/modificationExceptions',
            //         link: 'loans/modification-exceptions',
            //       },
            //     ],
            //   },
            // },
            {
              path: 'loans/loan-types',
              loadChildren: () =>
                import(
                  './features/loans/loan-management/loan-types/loan-types.route'
                ).then((m) => m.routes),
              data: {
                breadcrumbs: [
                  {
                    label: 'shared/loanTypes',
                    link: 'loans/loan-types',
                  },
                ],
              },
            },
            {
              path: 'loans/loan-categories',
              loadChildren: () =>
                import(
                  './features/loans/loan-management/loan-categories/loan-categories.route'
                ).then((m) => m.routes),
              canActivate: [permissionGuard],
              data: {
                breadcrumbs: [
                  {
                    label: 'shared/loanCategories',
                    link: 'loans/loan-categories',
                  },
                ],
              },
            },
            {
              path: 'loans/attachments',
              loadComponent: () =>
                import(
                  './features/loans/loan-management/attachments/attachments.component'
                ).then((m) => m.AttachmentsComponent),
              canActivate: [permissionGuard],
              data: {
                breadcrumbs: [
                  {
                    label: 'shared/attachments',
                    link: 'loans/attachments',
                  },
                ],
              },
            },
            {
              path: 'loans/deduction-entities',
              loadComponent: () =>
                import(
                  './features/loans/deduction-entities/deduction-entities.component'
                ).then((m) => m.DeductionEntitiesComponent),
              canActivate: [permissionGuard],
              data: {
                breadcrumbs: [
                  {
                    label: 'shared/deductionEntities',
                    link: 'loans/deduction-entities',
                  },
                ],
              },
            },
            {
              path: 'loans/insurances',
              loadComponent: () =>
                import(
                  './features/loans/insurances/insurances.component'
                ).then((m) => m.InsurancesComponent),
              canActivate: [permissionGuard],
              data: {
                breadcrumbs: [
                  {
                    label: 'shared/insurances',
                    link: 'loans/insurances',
                  },
                ],
              },
            },
            {
              path: 'loans/overview-committees',
              loadChildren: () =>
                import(
                  './features/loans/committees/overview-committees/overview-committees.route'
                ).then((m) => m.routes),
              data: {
                breadcrumbs: [
                  {
                    label: 'loans/overviewCommittees',
                    link: 'loans/overview-committees',
                  },
                ],
              },
            },
            {
              path: 'loans/committees-members',
              loadComponent: () =>
                import(
                  './features/loans/committees/committees-members/committees-members.component'
                ).then((m) => m.CommitteesMembersComponent),
              canActivate: [permissionGuard],
              data: {
                breadcrumbs: [
                  {
                    label: 'shared/committeesMembers',
                    link: 'loans/committees-members',
                  },
                ],
              },
            },
            {
              path: 'loans/committees-requests',
              loadChildren: () =>
                import(
                  './features/loans/committees/committees-requests/committees-requests.route'
                ).then((m) => m.routes),
              data: {
                breadcrumbs: [
                  {
                    label: 'shared/committeesRequests',
                    link: 'loans/committees-requests',
                  },
                ],
              },
            },
          ]),
      {
        path: 'users',
        loadChildren: () =>
          import('./features/users/users.routes').then((m) => m.routes),
        data: { breadcrumbs: [{ label: 'shared/affiliates', link: 'users' }] },
      },
      {
        path: 'relatives',
        loadComponent: () =>
          import('./features/relatives/relatives.component').then(
            (m) => m.RelativesComponent
          ),
        canActivate: [permissionGuard],
        data: {
          permissionId: PERMISSIONS_KEYS.access_relatives,
          breadcrumbs: [{ label: 'shared/relatives', link: 'relatives' }],
        },
      },
      {
        path: 'delegates',
        loadComponent: () =>
          import('./features/delegates/delegates.component').then(
            (m) => m.DelegatesComponent
          ),
        canActivate: [permissionGuard],
        data: {
          permissionId: PERMISSIONS_KEYS.access_delegates,
          breadcrumbs: [{ label: 'shared/delegates', link: 'delegates' }],
        },
      },
      {
        path: 'staff',
        loadComponent: () =>
          import('./features/staff/staff.component').then(
            (m) => m.StaffComponent
          ),
        canActivate: [permissionGuard],
        data: {
          permissionId: PERMISSIONS_KEYS.access_staff,
          breadcrumbs: [{ label: 'shared/staff', link: 'staff' }],
        },
      },
      {
        path: 'reports',
        loadComponent: () =>
          import('./features/reports/reports.component').then(
            (m) => m.ReportsComponent
          ),
        canActivate: [permissionGuard],
        data: {
          permissionId: PERMISSIONS_KEYS.access_reports,
          breadcrumbs: [{ label: 'shared/reports', link: 'reports' }],
        },
      },
      {
        path: 'news',
        loadChildren: () =>
          import('./features/news/news.routes').then((m) => m.routes),
        data: { breadcrumbs: [{ label: 'shared/news', link: 'news' }] },
      },
      {
        path: 'announcements',
        loadChildren: () =>
          import('./features/announcements/announcements.routes').then(
            (m) => m.routes
          ),
        data: {
          breadcrumbs: [
            { label: 'shared/announcements', link: 'announcements' },
          ],
        },
      },
      {
        path: 'events',
        loadChildren: () =>
          import('./features/events/events.routes').then((m) => m.routes),
        data: {
          breadcrumbs: [{ label: 'shared/events', link: 'events' }],
        },
      },
      {
        path: 'roles',
        loadChildren: () =>
          import('./features/roles/roles.routes').then((m) => m.routes),
        data: { breadcrumbs: [{ label: 'shared/roles', link: 'roles' }] },
      },
      {
        path: 'unauthorized',
        loadComponent: () =>
          import('./features/errors/unauthorized/unauthorized.component').then(
            (m) => m.UnauthorizedComponent
          ),
        data: {
          breadcrumbs: [{ label: 'shared/unauthorized', link: 'unauthorized' }],
        },
      },
      {
        path: 'not-found',
        loadComponent: () =>
          import('./features/errors/not-found/not-found.component').then(
            (m) => m.NotFoundComponent
          ),
      },
      {
        path: '**',
        loadComponent: () =>
          import('./features/errors/not-found/not-found.component').then(
            (m) => m.NotFoundComponent
          ),
      },
    ],
  },

  { path: '**', redirectTo: 'dashboard/not-found' },
];
